import { api } from './api';

/**
 * 订阅推送。
 *
 * ★ 整个过程**必须是静默失败的**。
 *
 *   浏览器可能不支持、用户可能拒绝、推送服务可能连不上
 *   (无头浏览器里必然连不上)—— 任何一步出问题都只意味着
 *   「这台设备收不到推送」,不该影响司机干活。
 *   一个通知订阅把首页搞白屏,是比没有推送糟得多的结果。
 *
 * ★ 没有 VAPID 公钥就**不订阅**。订阅一个没人会发的通道,
 *   只会白白弹一次权限请求 —— 而被拒之后再也要不回来。
 */
export async function subscribePush(publicKey: string | null): Promise<'ok' | 'skipped' | 'failed'> {
  if (!publicKey) return 'skipped';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'skipped';
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    // 权限是在「上线」那一刻要的,这里不主动弹
    return 'skipped';
  }

  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        // 浏览器规定:收了推送必须弹通知,否则会吊销权限
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    await api('/driver/me/push-subscription', {
      method: 'PUT',
      body: { subscription: sub.toJSON() },
    });
    return 'ok';
  } catch {
    return 'failed';
  }
}

/** 注销 —— 换设备或者关掉通知时用 */
export async function unsubscribePush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch {
    /* 本机注销失败也要把服务端那条清掉,否则会一直往死 endpoint 推 */
  }
  await api('/driver/me/push-subscription', {
    method: 'PUT',
    body: { subscription: null },
  }).catch(() => undefined);
}

/**
 * VAPID 公钥是 base64url,而 `applicationServerKey` 要的是字节数组。
 *
 * ★ 不能直接 atob:base64url 用的是 `-` `_`,标准 base64 是 `+` `/`,
 *   而且没有 `=` 补位。直接喂给 atob 会抛,或者更糟 —— 解出一串错的字节,
 *   订阅成功但服务端永远发不进去。
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  // 返回 ArrayBuffer:Uint8Array<ArrayBufferLike> 不满足 BufferSource
  return bytes.buffer;
}
